import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import type { CodeEditorAdvancedProps } from './internal/CodeEditorAdvanced/CodeEditorAdvanced';
import { type ModeLoadingState, loadMode } from './internal/CodeEditorAdvanced/modes/lazyModeLoader';
import { CodeEditorFallback } from './internal/CodeEditorFallback';
import { CodeEditorRetryFallback } from './internal/CodeEditorRetryFallback';

const CodeEditorAdvanced = lazy(async () =>
  import(/* webpackChunkName: "CodeEditorAdvanced" */ './internal/CodeEditorAdvanced/CodeEditorAdvanced').then(
    (module) => ({
      default: module.CodeEditorAdvanced,
    }),
  ),
);

type LazyCodeEditorAdvancedProps = Omit<CodeEditorAdvancedProps, 'modeConfig'> & {
  fullscreen?: boolean;
};

// Component that manages mode loading state
const ModeLoader = (props: LazyCodeEditorAdvancedProps) => {
  const { mode = 'json', fullscreen = true } = props;
  const isCancelled = useRef(false);

  // State for tracking mode loading
  const [modeLoadingState, setModeLoadingState] = useState<ModeLoadingState>({
    isLoading: true,
    mode: undefined,
  });

  const loadModeAsync = useCallback(async () => {
    isCancelled.current = false;
    setModeLoadingState({ isLoading: true, mode: undefined });

    try {
      const modeConfig = await loadMode(mode);

      if (!isCancelled.current) {
        setModeLoadingState({
          isLoading: false,
          mode: modeConfig,
        });
      }
    } catch (error) {
      if (!isCancelled.current) {
        setModeLoadingState({
          isLoading: false,
          error: error instanceof Error ? error : new Error('Failed to load mode'),
          mode: undefined,
        });
      }
    }
  }, [mode]);

  // Load the mode asynchronously when the component mounts or mode changes
  useEffect(() => {
    void loadModeAsync();

    return () => {
      isCancelled.current = true;
    };
  }, [loadModeAsync]);

  // Show loading state while mode is loading
  if (modeLoadingState.isLoading) {
    return <CodeEditorFallback fullscreen={fullscreen} />;
  }

  // Show error state if mode failed to load
  if (modeLoadingState.error) {
    return <CodeEditorRetryFallback resetErrorBoundary={loadModeAsync} />;
  }

  // Render the editor with the loaded mode
  if (modeLoadingState.mode) {
    return (
      <Suspense fallback={<CodeEditorFallback fullscreen={fullscreen} />}>
        <CodeEditorAdvanced {...(props as CodeEditorAdvancedProps)} modeConfig={modeLoadingState.mode} />
      </Suspense>
    );
  }

  // Fallback (shouldn't happen, but just in case)
  return <CodeEditorFallback fullscreen={fullscreen} />;
};

export const LazyCodeEditorAdvanced = (props: LazyCodeEditorAdvancedProps) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <ErrorBoundary
        severity="medium"
        onReset={reset}
        fallbackRender={({ resetErrorBoundary }) => <CodeEditorRetryFallback resetErrorBoundary={resetErrorBoundary} />}
      >
        <ModeLoader {...props} />
      </ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
);
